// common/services/registration/getRegistrationAdmin.ts
import req from '../../api/request'
import type { Tax } from '../../types/Registration'

type IdName = {
  id: string
  name: string
}

/** Waiver as returned inside the admin payload (the *selected* waivers). */
export type RegistrationAdminWaiver = {
  id: string
  name: string
  waiver_content?: string | null
}

/**
 * Admin-scoped registration payload — GET /registration/:scheduleId/admin.
 *
 * The single hydration source for the Registration Setup modal. Typed from the
 * observed response rather than reusing `Registration` from common/types: that
 * type is loose where this flow needs precision (`player_form` / `team_form`
 * are `any`, and the confirmation-email fields are typed optional despite
 * always being present, which forced `as any` casts at the call site).
 *
 * Only the fields the setup modal actually reads are declared. The payload
 * carries more (root_schedule detail, division metadata); add fields here as
 * consumers appear rather than mirroring the whole response.
 */
export type RegistrationAdminData = {
  id: string
  name: string
  schedule_id: string

  // Registration type toggles
  is_team_registration: boolean
  is_player_registration: boolean
  auto_approve: boolean
  can_add_players_from_roster: boolean
  registration_type_id?: number

  // Dates
  starts_at: string | null
  ends_at: string | null

  // Currency / payment
  currency_country?: { id: number | string } | null
  payment_provider_type?: IdName | null
  payment_type?: { id: string; name: string } | null

  // Taxes: `available_taxes` is the selectable catalogue, `taxes` the selection.
  available_taxes?: Tax[]
  taxes?: Array<{ id: string }>

  // Waivers: `show_waiver` toggles the step, `waivers` is the selection.
  show_waiver: boolean
  waivers?: RegistrationAdminWaiver[]

  // Customization
  dark_theme: boolean
  image?: unknown
  header_content: string | null
  team_confirmation_email_content: string | null
  player_confirmation_email_content: string | null
  bcc_registrations_to: string | null

  // Custom forms
  player_form?: unknown
  team_form?: unknown

  // Processing fees (SN-8002)
  payment_processing_fee_bearer?: string | null
  payment_processing_fee_visible?: boolean | null
  league_processing_fee_percent?: number | null
  league_processing_fee_flat?: number | null
  league_processing_fee_visible?: boolean | null
  /**
   * When true, a refund returns the registrant's full payment and the payment
   * provider's processing fee is absorbed by the league's account instead.
   */
  refund_payment_processing_fee?: boolean | null

  /**
   * True once the competition has taken any registration. Locks the Currency
   * field. Replaces the modal's former /admin/analytics fetch, which pulled six
   * metric groups to evaluate a single `> 0`.
   */
  has_registrations?: boolean

  root_schedule?: {
    id: string
    name: string | null
    name_full: string | null
    registration_version?: number
  } | null
}

type GetRegistrationAdminParams = {
  scheduleId: string
  signal?: AbortSignal
}

export const getRegistrationAdmin = async ({
  scheduleId,
  signal,
}: GetRegistrationAdminParams): Promise<RegistrationAdminData | null> => {
  const response = await req(`/registration/${scheduleId}/admin`, { signal })
  return (response?.data as RegistrationAdminData) ?? null
}
